<template>
  <div>
    <transition name="modal">
      <confirmation-modal
        v-if="reportIdToRegenerate"
        :message="'¿Estás seguro que deseas regenerar este reporte?'"
        @close="reportIdToRegenerate = null"
        @confirm="regenerateReport"
      ></confirmation-modal>
    </transition>

    <div v-if="!loading">
      <!-- Year selector -->
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <button
          v-for="(year, index) in availableYears"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedYear === year
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectYear(year)"
        >
          {{ year }}
        </button>
      </div>

      <!-- Month selector -->
      <div class="flex flex-wrap items-center gap-2 mb-6">
        <button
          v-for="(month, index) in availableMonths"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedMonth === month.number
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectMonth(month)"
        >
          {{ month.label }}
        </button>
      </div>

      <div v-if="report">
        <!-- Biweekly period selector — more prominent pill buttons -->
        <div class="mb-6">
          <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Período quincenal</h2>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="biweeklyReport in report.reports"
              :key="biweeklyReport.id"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors',
                selectedReportId === biweeklyReport.id
                  ? 'bg-primary-500 border-primary-500 text-white shadow'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-primary-500 hover:text-primary-500',
              ]"
              @click="selectReport(biweeklyReport)"
            >
              {{ formatShortDate(biweeklyReport.start_date) }} &mdash; {{ formatShortDate(biweeklyReport.end_date) }}
            </button>
          </div>
        </div>

        <!-- Selected period detail -->
        <div v-if="selectedReport">
          <!-- Empty state -->
          <div
            v-if="!selectedReport.franchise_biweekly_reports || selectedReport.franchise_biweekly_reports.length === 0"
            class="card text-center py-6"
          >
            <p class="text-content-300">Sin datos para este período</p>
          </div>

          <!-- Franchise table -->
          <div v-else class="bg-white rounded-lg shadow overflow-hidden mb-6">
            <div class="px-5 py-3 border-b border-gray-100">
              <h2 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Regalías por Franquicia</h2>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead class="bg-primary-100">
                  <tr>
                    <th class="px-5 py-3 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Franquicia</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Planes bruto</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Cupón 1ª compra</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Monedero</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Planes neto</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Reembolsos</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Tarjeta/Efectivo</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Comisión</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Regalía a cobrar</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Venta neta (a recibir)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(franchiseReport, fIndex) in selectedReport.franchise_biweekly_reports"
                    :key="franchiseReport.id"
                    :class="fIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  >
                    <td class="px-5 py-3 font-semibold text-gray-800">{{ franchiseReport.franchise.name }}</td>
                    <td class="px-4 py-3 text-right text-gray-700">
                      {{
                        integerToCurrency(
                          franchiseReport.stripe_amount +
                            (franchiseReport.coupon_discount_amount || 0) +
                            (franchiseReport.wallet_applied_amount || 0)
                        )
                      }}
                    </td>
                    <td class="px-4 py-3 text-right text-error-500">{{ franchiseReport.coupon_discount_amount > 0 ? '− ' + integerToCurrency(franchiseReport.coupon_discount_amount) : '—' }}</td>
                    <td class="px-4 py-3 text-right text-error-500">{{ franchiseReport.wallet_applied_amount > 0 ? '− ' + integerToCurrency(franchiseReport.wallet_applied_amount) : '—' }}</td>
                    <td class="px-4 py-3 text-right text-gray-700">{{ integerToCurrency(franchiseReport.stripe_amount) }}</td>
                    <td class="px-4 py-3 text-right text-error-500">{{ integerToCurrency(franchiseReport.stripe_refunded_amount) }}</td>
                    <td class="px-4 py-3 text-right text-gray-700">{{ integerToCurrency(franchiseReport.incomes_amount) }}</td>
                    <td class="px-4 py-3 text-right text-gray-600">7.5%</td>
                    <td class="px-4 py-3 text-right font-semibold text-success-500">{{ integerToCurrency(franchiseReport.total) }}</td>
                    <td class="px-4 py-3 text-right font-semibold text-primary-500">
                      {{
                        integerToCurrency(
                          franchiseReport.stripe_amount -
                            franchiseReport.stripe_refunded_amount +
                            franchiseReport.incomes_amount
                        )
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="px-5 py-3 text-xs text-gray-500 border-t border-gray-100">
              La regalía se calcula sobre la <strong>venta neta</strong>: lo que la franquicia
              realmente recibió después del cupón de primera compra (costo de adquisición, lo
              absorbe la sucursal que captó al cliente) y del monedero canjeado (costo de
              retención, lo absorbe la sucursal donde se canjeó). Ninguno de los dos genera regalía.
            </p>
          </div>

          <!-- Regenerate action -->
          <div class="flex justify-end">
            <button
              class="delete-button"
              @click.prevent="reportIdToRegenerate = selectedReport.id"
            >
              Regenerar Reporte
            </button>
          </div>
        </div>

        <!-- Prompt to select a period -->
        <div v-else class="card text-center py-6">
          <p class="text-content-300">Selecciona un período quincenal para ver el detalle.</p>
        </div>
      </div>

      <!-- Prompt to select a month -->
      <div v-else-if="selectedMonth === null" class="card text-center py-10">
        <p class="text-content-300">Selecciona un mes para ver el reporte.</p>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import api from "@/api";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Biweekly",

  components: {
    ConfirmationModal,
  },

  data() {
    return {
      report: null,
      loading: false,
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      selectedReportId: null,
      months: [
        { number: 1, label: "Enero" },
        { number: 2, label: "Febrero" },
        { number: 3, label: "Marzo" },
        { number: 4, label: "Abril" },
        { number: 5, label: "Mayo" },
        { number: 6, label: "Junio" },
        { number: 7, label: "Julio" },
        { number: 8, label: "Agosto" },
        { number: 9, label: "Septiembre" },
        { number: 10, label: "Octubre" },
        { number: 11, label: "Noviembre" },
        { number: 12, label: "Diciembre" },
      ],
      reportIdToRegenerate: null,
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const startingYear = 2020;
      const years = [];

      for (let year = startingYear; year <= currentYear; year++) {
        years.push(year);
      }

      return years;
    },

    availableMonths() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (this.selectedYear === currentYear) {
        return this.months.filter((month) => month.number <= currentMonth);
      }

      return this.months;
    },

    selectedReport() {
      if (!this.selectedReportId) {
        return null;
      }

      return this.report.reports.filter(
        (report) => report.id === this.selectedReportId
      )[0];
    },
  },

  created() {
    this.loadReport();
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    formatShortDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },

    async loadReport() {
      this.loading = true;

      try {
        this.report = await api.reports.getBiweeklyReport([
          { name: "year", value: this.selectedYear },
          {
            name: "month",
            value: this.selectedMonth,
          },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    selectYear(year) {
      if (year === this.selectedYear) {
        return;
      }

      this.selectedYear = year;
      this.selectedMonth = null;
      this.report = null;
    },

    selectMonth(month) {
      if (this.selectedMonth === month) {
        return;
      }

      this.selectedMonth = month.number;

      this.loadReport();
    },

    selectReport(report) {
      if (this.selectedReportId) {
        if (this.selectedReportId === report.id) {
          this.selectedReportId = null;
        } else {
          this.selectedReportId = report.id;
        }
      } else {
        this.selectedReportId = report.id;
      }
    },

    async regenerateReport() {
      this.loading = true;

      try {
        const response = await api.reports.regenerateBiweeklyReport(
          this.reportIdToRegenerate
        );
        this.$store.dispatch("successSnack", response.message);
        this.loadReport();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.reportIdToRegenerate = null;
      this.loading = false;
    },
  },
};
</script>
